/**
 * Calendar event data layer — extracted from `CalendarPage.tsx` so
 * the dashboard hooks (`useApplicantDashboard`, `useAdminDashboard`)
 * can import the upcoming-events helpers without pulling in the
 * full calendar UI component.
 *
 * Spec: meridian-redesign — PR (n) Bundle splitting
 * Requirements: 14.1, 18.5
 */

import { generateClient } from 'aws-amplify/api';
import { listCalendarEvents } from '@/graphql/queries';

export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // ISO date string (start date)
  time: string; // start time; ignored when allDay
  endDate?: string;
  endTime?: string;
  allDay?: boolean;
  location: string;
  type: 'Meeting' | 'Rehearsal' | 'Appearance' | 'Deadline' | 'Public';
  description?: string;
  isCountdownAnchor?: boolean;
  createdBy?: string;
}

const EVENTS_STORAGE_KEY = 'idaho-pageant-events';

/** Write events to localStorage cache for dashboard widget. */
export function cacheEvents(events: CalendarEvent[]): void {
  try {
    localStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(events));
  } catch {
    // Storage may be unavailable in private browsing or quota-exceeded.
  }
}

/** Read cached events from localStorage. */
export function getCachedEvents(): CalendarEvent[] {
  try {
    const raw = localStorage.getItem(EVENTS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Get upcoming events synchronously from localStorage cache.
 * Used by dashboard widgets that render immediately on first paint
 * before the async fetch completes.
 */
export function getUpcomingEvents(): CalendarEvent[] {
  const events = getCachedEvents();
  const today = new Date().toISOString().split('T')[0];
  return events
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5);
}

/** Map a GraphQL CalendarEvent result to the frontend interface. */
export function mapGqlEvent(e: {
  id: string;
  title: string;
  date: string;
  time: string;
  endDate?: string | null;
  endTime?: string | null;
  allDay?: boolean | null;
  location: string;
  type?: string | null;
  description?: string | null;
  isCountdownAnchor?: boolean | null;
  createdBy?: string | null;
}): CalendarEvent {
  return {
    id: e.id,
    title: e.title,
    date: e.date,
    time: e.time,
    endDate: e.endDate ?? undefined,
    endTime: e.endTime ?? undefined,
    allDay: Boolean(e.allDay),
    location: e.location,
    type: (e.type as CalendarEvent['type']) || 'Meeting',
    description: e.description ?? undefined,
    isCountdownAnchor: Boolean(e.isCountdownAnchor),
    createdBy: e.createdBy ?? undefined,
  };
}

/**
 * Pure helper that filters a list of calendar events to those on
 * or after today, sorts them by ascending start date, and caps
 * the result at `max` entries.
 *
 * Extracted as a named export so dashboard hooks can call it and
 * property-based tests can exercise it without spinning up the
 * full hook (which depends on Amplify GraphQL).
 *
 * Spec: contestant-admin-ui-polish — Task 3.1
 * Requirements: 2.1, 2.2, 2.3
 */
export function limitUpcomingEvents(
  events: CalendarEvent[],
  max: number = 3,
): CalendarEvent[] {
  if (max <= 0) return [];
  const today = new Date().toISOString().split('T')[0];
  return events
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, max);
}

/**
 * Fetch upcoming events directly from GraphQL.
 * Used in dashboards to ensure events are available even if
 * `CalendarPage` hasn't been visited yet.
 *
 * Season SSOT (ssot-cutover-cleanup R-A): this module cannot call the
 * `useActiveSeason` hook, so the operative competition year is passed IN
 * as an argument by the React/handler caller (which resolves it through
 * the active-season seam). The module never re-derives the year itself.
 */
export async function fetchUpcomingEventsFromApi(
  operativeYear: number,
): Promise<CalendarEvent[]> {
  try {
    const apiClient = generateClient();
    const result = (await apiClient.graphql({
      query: listCalendarEvents,
      variables: { competitionYear: operativeYear },
      authMode: 'apiKey',
    })) as { data?: { listCalendarEvents?: unknown[] } };
    const fetched = (result.data?.listCalendarEvents ?? [])
      .map((e) => mapGqlEvent(e as Parameters<typeof mapGqlEvent>[0]));
    cacheEvents(fetched);
    const today = new Date().toISOString().split('T')[0];
    return fetched
      .filter((e: CalendarEvent) => e.date >= today)
      .sort((a: CalendarEvent, b: CalendarEvent) => a.date.localeCompare(b.date));
  } catch {
    return getUpcomingEvents();
  }
}
